import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Mail,
  Send,
  Sparkles,
  Users,
  DollarSign,
  Copy,
  AlertCircle,
  Megaphone,
  ArrowRight,
} from "lucide-react";

import "./HostDashboard.css";

const TIER_OPTIONS = [
  { id: "main", label: "Main event sponsor" },
  { id: "table", label: "Table sponsor" },
];

const SAMPLE_MESSAGE_SPONSOR = (host, eventTitle) =>
  `Hi {sponsor name},\n\n` +
  `I'm ${host || "{your name}"} and I'm launching a virtual event on vmeetio called ${eventTitle || "{event title}"}.\n\n` +
  `I'd love to invite you as a brand sponsor. We're inviting select brands who care about the conversation in this room — and vmeetio's format gives sponsors real, named visibility next to the conversation (not interstitial ads).\n\n` +
  `Have a look at the options below — Main event sponsor is the lead position; Table sponsor lets you anchor one specific roundtable with your own moderator (free of charge).\n\n` +
  `Would love to chat.\n\n— ${host || "{your name}"}`;

const SAMPLE_MESSAGE_ATTENDEE = (host, eventTitle) =>
  `Hi {first name},\n\n` +
  `I'm ${host || "{your name}"} and I'm hosting a virtual roundtable on vmeetio: ${eventTitle || "{event title}"}.\n\n` +
  `Reserved your seat — would love you in the room. It's small, candid, and the table I'd put you on already has people from your space.\n\n` +
  `One click below to confirm. See you there?\n\n— ${host || "{your name}"}`;

/* ----------------------------
   STATIC DATA (no APIs)
----------------------------- */
const nowIso = () => new Date().toISOString();
const addDays = (d) => new Date(Date.now() + d * 86400000).toISOString();

const maskEmail = (email) => {
  const e = String(email || "").trim();
  const [u, d] = e.split("@");
  if (!u || !d) return e || "—";
  const head = u.slice(0, 1);
  return `${head}${u.length > 1 ? "•••" : ""}@${d}`;
};

const makeInvite = ({ event_id, audience, email, tiers }) => ({
  id: `inv_${Math.random().toString(16).slice(2)}`,
  event_id,
  audience,
  recipient_email_masked: maskEmail(email),
  sent_at: nowIso(),
  opened_at: null,
  converted_at: null,
  tiers: tiers || [],
});

const STATIC_HOST_TOKENS = {
  DEMO: {
    name: "Alex",
    email: "alex@host.example",
    organisation: "vmeetio",
    expires_at: addDays(14),
    events: [
      {
        id: 1,
        title: "AI After Hours",
        cover:
          "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=80",
        start_time: addDays(2),
        end_time: addDays(2),
        is_open_for_sponsorship: true,
        kpis: {
          registrations: 184,
          tables: 3,
          sponsorship_revenue: 4800,
          invites_sent: 6,
          invites_opened: 3,
          invites_converted: 1,
        },
      },
      {
        id: 2,
        title: "Climate Capital Salon",
        cover:
          "https://images.unsplash.com/photo-1509395176047-4a66953fd231?auto=format&fit=crop&w=800&q=80",
        start_time: addDays(8),
        end_time: addDays(8),
        is_open_for_sponsorship: false,
        kpis: {
          registrations: 122,
          tables: 2,
          sponsorship_revenue: 12000,
          invites_sent: 2,
          invites_opened: 1,
          invites_converted: 0,
        },
      },
    ],
  },
};

const STATIC_INVITES_BY_TOKEN = {
  DEMO: [
    {
      id: "i1",
      event_id: 1,
      audience: "sponsor",
      recipient_email_masked: maskEmail("partners@anyonai.example"),
      sent_at: addDays(-1).slice(0, 16),
      opened_at: addDays(-1).slice(0, 16),
      converted_at: null,
    },
    {
      id: "i2",
      event_id: 1,
      audience: "attendee",
      recipient_email_masked: maskEmail("jordan@example.com"),
      sent_at: addDays(-1).slice(0, 16),
      opened_at: addDays(-1).slice(0, 16),
      converted_at: addDays(-1).slice(0, 16),
    },
    {
      id: "i3",
      event_id: 2,
      audience: "sponsor",
      recipient_email_masked: maskEmail("hello@greentide.example"),
      sent_at: addDays(-2).slice(0, 16),
      opened_at: null,
      converted_at: null,
    },
  ],
};

export default function HostDashboard() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") || "").toUpperCase();

  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [refreshAt, setRefreshAt] = useState(0);

  useEffect(() => {
    if (!token) {
      setError(
        "Missing host token. Please open the link from your approval email.",
      );
      return;
    }
    const host = STATIC_HOST_TOKENS[token];
    if (!host) {
      setError("Your host link is invalid or expired.");
      return;
    }
    setError("");
    setMe(JSON.parse(JSON.stringify(host)));
    setInvites(
      JSON.parse(JSON.stringify(STATIC_INVITES_BY_TOKEN[token] || [])),
    );
  }, [token, refreshAt]);

  const applyInviteKpi = (eventId, delta) => {
    setMe((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        events: (prev.events || []).map((e) => ({
          ...e,
          kpis: { ...(e.kpis || {}) },
        })),
      };
      const idx = next.events.findIndex((e) => e.id === eventId);
      if (idx >= 0) {
        next.events[idx].kpis.invites_sent =
          Number(next.events[idx].kpis.invites_sent || 0) + Number(delta || 0);
      }
      return next;
    });
  };

  const addInvites = (eventId, audience, recipientEmails, tiers) => {
    const toAdd = (recipientEmails || []).map((email) =>
      makeInvite({ event_id: eventId, audience, email, tiers }),
    );
    setInvites((prev) => [...toAdd, ...prev]);
    applyInviteKpi(eventId, toAdd.length);
  };

  if (error) {
    return (
      <main className="hostDashErrorMain" data-testid="host-error">
        <div className="hostDashErrorCard">
          <AlertCircle size={20} className="hostDashErrorIcon" />
          <p className="font-display hostDashErrorTitle">
            Can't open host dashboard
          </p>
          <p className="hostDashErrorBody">{error}</p>
        </div>
      </main>
    );
  }

  if (!me) {
    return (
      <main className="hostDashLoadingMain">
        <Loader2 size={20} className="hostDashSpinner" />
      </main>
    );
  }

  return (
    <main className="hostDashMain" data-testid="host-dashboard">
      <div className="hostDashHeader">
        <div>
          <p className="overline hostDashHeaderOverline">Host dashboard</p>
          <h1 className="font-display hostDashH1">Hi {me.name || "host"}.</h1>
          <p className="hostDashHeaderMeta">
            {me.email}
            {me.organisation ? ` · ${me.organisation}` : ""} · access expires{" "}
            {me.expires_at?.slice(0, 10)}
          </p>
        </div>

        <div className="hostDashHeaderHelp">
          <p>Need help? Reply to your approval email.</p>
        </div>
      </div>

      {(me.events || []).length === 0 && (
        <div className="hostDashNoEvents" data-testid="host-no-events">
          <p className="hostDashNoEventsTitle">
            You don't have any events assigned to this token yet.
          </p>
          <p className="hostDashNoEventsBody">
            Ask the admin to link your event_id to your host token.
          </p>
        </div>
      )}

      <div className="hostDashEvents">
        {(me.events || []).map((ev) => (
          <HostEventCard
            key={ev.id}
            ev={ev}
            host={me}
            token={token}
            invites={invites.filter((i) => i.event_id === ev.id)}
            onSent={() => setRefreshAt(Date.now())}
            busy={busy}
            setBusy={setBusy}
            onAddInvites={addInvites}
          />
        ))}
      </div>
    </main>
  );
}

function HostEventCard({
  ev,
  host,
  token,
  invites,
  onSent,
  busy,
  setBusy,
  onAddInvites,
}) {
  const kpi = ev.kpis || {};
  const [pane, setPane] = useState(null); // "sponsor" | "attendee" | null

  return (
    <section className="hostEventCard" data-testid={`host-event-card-${ev.id}`}>
      <div className="hostEventTop">
        {ev.cover && <img src={ev.cover} alt="" className="hostEventCover" />}

        <div className="hostEventInfo">
          <p className="overline hostEventDate">
            {ev.start_time?.slice(0, 10)}
            {ev.end_time ? ` → ${ev.end_time?.slice(0, 10)}` : ""}
          </p>
          <h2 className="font-display hostEventTitle">{ev.title}</h2>
          <p className="hostEventSponsorshipState">
            {ev.is_open_for_sponsorship ? (
              <span className="hostEventSponsorshipOpen">
                Sponsorship cart open
              </span>
            ) : (
              <span>Sponsorship cart closed</span>
            )}
          </p>
        </div>

        <div className="hostEventTopActions">
          <Link
            to={`/events/${ev.id}`}
            target="_blank"
            rel="noreferrer"
            className="hostEventViewBtn"
            data-testid={`host-view-event-${ev.id}`}
          >
            View public page{" "}
            <ArrowRight size={11} className="hostEventViewArrow" />
          </Link>
        </div>
      </div>

      <div className="hostEventKpis">
        <Kpi
          icon={Users}
          label="Registrations"
          value={kpi.registrations || 0}
          testId={`kpi-registrations-${ev.id}`}
        />
        <Kpi
          icon={Sparkles}
          label="Tables"
          value={kpi.tables || 0}
          testId={`kpi-tables-${ev.id}`}
        />
        <Kpi
          icon={DollarSign}
          label="Sponsorship revenue"
          value={`$${(kpi.sponsorship_revenue || 0).toLocaleString()}`}
          testId={`kpi-revenue-${ev.id}`}
        />
        <Kpi
          icon={Megaphone}
          label="Invites · sent / opened / converted"
          value={`${kpi.invites_sent || 0} / ${kpi.invites_opened || 0} / ${
            kpi.invites_converted || 0
          }`}
          testId={`kpi-invites-${ev.id}`}
        />
      </div>

      <div className="hostEventToolbar">
        <button
          type="button"
          onClick={() => setPane(pane === "sponsor" ? null : "sponsor")}
          className={`hostEventToggleBtn ${
            pane === "sponsor" ? "isActive" : "isInactive"
          }`}
          data-testid={`host-invite-sponsors-toggle-${ev.id}`}
        >
          <Mail size={12} /> Invite sponsors
        </button>

        <button
          type="button"
          onClick={() => setPane(pane === "attendee" ? null : "attendee")}
          className={`hostEventToggleBtn ${
            pane === "attendee" ? "isActive" : "isInactive"
          }`}
          data-testid={`host-invite-attendees-toggle-${ev.id}`}
        >
          <Users size={12} /> Invite attendees
        </button>

        <CopyButton
          text={`${window.location.origin}/events/${ev.id}`}
          label="Copy event link"
          testId={`host-copy-event-${ev.id}`}
        />

        {ev.is_open_for_sponsorship && (
          <CopyButton
            text={`${window.location.origin}/sponsor?event=${ev.id}`}
            label="Copy sponsor cart link"
            testId={`host-copy-sponsor-${ev.id}`}
          />
        )}
      </div>

      {pane && (
        <InvitePane
          ev={ev}
          host={host}
          token={token}
          audience={pane}
          onSent={onSent}
          busy={busy}
          setBusy={setBusy}
          onAddInvites={onAddInvites}
        />
      )}

      {invites.length > 0 && (
        <div className="hostInviteLog" data-testid={`host-invite-log-${ev.id}`}>
          <p className="overline hostInviteLogOverline">
            Recent invites · {invites.length}
          </p>

          <ul className="hostInviteList">
            {invites.slice(0, 25).map((i) => (
              <li key={i.id} className="hostInviteRow">
                <span className="hostInviteText">
                  <span className="hostInviteAudience">{i.audience}</span> ·{" "}
                  {i.recipient_email_masked} · sent{" "}
                  {String(i.sent_at || "")
                    .slice(0, 16)
                    .replace("T", " ")}
                </span>

                <span className="hostInviteStatus">
                  {i.converted_at ? (
                    <span className="hostInviteStatusConverted">converted</span>
                  ) : i.opened_at ? (
                    <span className="hostInviteStatusOpened">opened</span>
                  ) : (
                    <span className="hostInviteStatusSent">sent</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function InvitePane({
  ev,
  host,
  token,
  audience,
  onSent,
  busy,
  setBusy,
  onAddInvites,
}) {
  const [recipients, setRecipients] = useState("");
  const [message, setMessage] = useState(() =>
    audience === "sponsor"
      ? SAMPLE_MESSAGE_SPONSOR(host.name, ev.title)
      : SAMPLE_MESSAGE_ATTENDEE(host.name, ev.title),
  );
  const [tiers, setTiers] = useState(["main", "table"]);

  const parsed = useMemo(() => {
    const set = new Set();
    (recipients || "")
      .split(/[,\n;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .forEach((s) => {
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) set.add(s);
      });
    return Array.from(set);
  }, [recipients]);

  const send = async () => {
    if (parsed.length === 0) {
      toast.error("Add at least one valid email");
      return;
    }
    if (!message.trim()) {
      toast.error("Message is required");
      return;
    }
    if (audience === "sponsor" && tiers.length === 0) {
      toast.error("Pick at least one sponsor tier");
      return;
    }

    setBusy(true);
    try {
      onAddInvites?.(
        ev.id,
        audience,
        parsed.slice(0, 100),
        audience === "sponsor" ? tiers : [],
      );
      toast.success(
        `Sent ${Math.min(100, parsed.length)} invite${
          Math.min(100, parsed.length) === 1 ? "" : "s"
        }`,
      );
      setRecipients("");
      onSent?.();
    } catch {
      toast.error("Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="hostInvitePane"
      data-testid={`host-invite-pane-${ev.id}-${audience}`}
    >
      <div className="hostInvitePaneGrid">
        <div>
          <label className="overline hostInviteLabel">Recipients</label>
          <textarea
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder="Paste emails — comma, space or newline separated"
            rows={4}
            className="hostInviteRecipients"
            data-testid={`host-recipients-${ev.id}-${audience}`}
          />
          <p className="hostInviteHint">
            {parsed.length === 0
              ? "No valid emails yet"
              : `${parsed.length} valid email${parsed.length === 1 ? "" : "s"} ready`}{" "}
            · max 100 per send
          </p>

          {audience === "sponsor" && (
            <div className="hostInviteTiers">
              <label className="overline hostInviteLabel">Tiers to offer</label>
              <div className="hostInviteTierPills">
                {TIER_OPTIONS.map((t) => (
                  <label
                    key={t.id}
                    className={`hostInviteTierPill ${
                      tiers.includes(t.id) ? "isOn" : "isOff"
                    }`}
                    data-testid={`host-tier-${ev.id}-${t.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={tiers.includes(t.id)}
                      onChange={(e) =>
                        setTiers((prev) =>
                          e.target.checked
                            ? [...prev, t.id]
                            : prev.filter((x) => x !== t.id),
                        )
                      }
                      className="hostInviteHiddenCheckbox"
                    />
                    {t.label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="overline hostInviteLabel">Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={10}
            className="hostInviteMessage font-mono"
            data-testid={`host-message-${ev.id}-${audience}`}
          />
          <p className="hostInviteHint">
            A signed CTA button to{" "}
            {audience === "sponsor" ? "sponsor checkout" : "event registration"}{" "}
            is auto-appended.
          </p>
        </div>
      </div>

      <div className="hostInvitePaneFooter">
        <p className="hostInviteFooterNote">
          Each recipient receives a personalised email with your name + an
          open-tracking pixel. Opens / conversions appear in this dashboard.
        </p>

        <button
          onClick={send}
          disabled={busy || parsed.length === 0 || !message.trim()}
          data-testid={`host-send-${ev.id}-${audience}`}
          className="hostInviteSendBtn"
          type="button"
        >
          {busy ? (
            <Loader2 size={12} className="hostDashSpinnerSmall" />
          ) : (
            <Send size={12} />
          )}
          Send {parsed.length || ""} invite{parsed.length === 1 ? "" : "s"}
        </button>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <div className="hostKpi" data-testid={testId}>
      <div className="hostKpiTop">
        {Icon && <Icon size={12} className="hostKpiIcon" />}
        <p className="overline hostKpiLabel">{label}</p>
      </div>
      <p className="font-display hostKpiValue">{value}</p>
    </div>
  );
}

function CopyButton({ text, label, testId }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testId}
      className="hostCopyBtn"
    >
      <Copy size={12} /> {copied ? "Copied" : label}
    </button>
  );
}
